import React, { Dispatch, SetStateAction, useCallback } from "react";

import { Button, FormControl, Modal, ModalClose, ModalDialog, Stack, Textarea } from "@mui/joy";
import { TableViewOutlined, AttachFileRounded, SendRounded } from "@mui/icons-material";

import { FileWithPath, useDropzone } from "react-dropzone";
import { FileUpload, FileUploadProps } from "./FileUpload";
import { useIsAdmin } from "../hooks/useIsAdmin";
import SpreadsheetFlowBuilder from "./workflow/SpreadsheetFlow";
import { useCanGenerateSpreadsheet } from "../hooks/configuration";
import { GenerateSpreadsheetArgs } from "../shared/interfaces";
import { FileApi } from "../api/file";

export type MessageInputProps = {
    uploadedFiles: FileUploadProps[];
    setUploadedFiles: Dispatch<SetStateAction<FileUploadProps[]>>;

    onSubmit: (value: string) => void;
    onSubmitGenerateSpreadsheet: (args: GenerateSpreadsheetArgs) => void;
};

export default function MessageInput(props: MessageInputProps) {
    const { uploadedFiles, setUploadedFiles, onSubmit } = props;

    const [textAreaValue, setTextAreaValue] = React.useState("");
    const textAreaRef = React.useRef<HTMLDivElement>(null);

    const handleSendClick = () => {
        if (!fileUploadInProgress && textAreaValue.trim() !== "") {
            onSubmit(textAreaValue.trim());
            setTextAreaValue("");
            setUploadedFiles([]);
        }
    };

    const [generateSpreadsheetModalOpen, setGenerateSpreadsheetModalOpen] = React.useState(false);

    const showGenerateSpreadsheet = useCanGenerateSpreadsheet();

    // BEGIN - File upload
    // Only admins can upload files for now
    const showFileUpload = useIsAdmin();
    const fileUploadInProgress = uploadedFiles.find((f) => f.progress !== 100) != null;
    const handleDrop = async (acceptedFiles: FileWithPath[]) => {
        // Add the pending files
        const newFiles = acceptedFiles.map(
            (file: FileWithPath, i: number): FileUploadProps => ({
                id: file.path!,
                name: file.name,
                progress: 0,
                index: i,
                createdAt: new Date(),
                size: file.size,
            }),
        );

        let newFileUploads = [...uploadedFiles, ...newFiles];
        setUploadedFiles(newFileUploads);

        // Upload all files concurrently
        const uploadPromises = acceptedFiles.map(async (file) => {
            try {
                const updateProgress = (fileId: string, progress: number) => {
                    setUploadedFiles((prev) =>
                        prev.map((f) => (f.id === file.path ? { ...f, progress } : f)),
                    );
                };

                const fileUpload = await FileApi.uploadFile(file, updateProgress);
                setUploadedFiles((prev) =>
                    prev.map((f) =>
                        f.id === file.path
                            ? {
                                  id: fileUpload.id,
                                  name: fileUpload.fileName,
                                  progress: 100,
                                  index: f.index,
                                  createdAt: fileUpload.createdAt,
                                  size: fileUpload.fileSize,
                              }
                            : f,
                    ),
                );
            } catch (e) {
                // Update the file status to show error
                setUploadedFiles((prev) =>
                    prev.map((f) => (f.id === file.path ? { ...f, progress: -1 } : f)),
                );
                console.error("File upload failed:", e);
            }
        });

        // Wait for all uploads to complete
        await Promise.all(uploadPromises);
    };
    const { getRootProps, getInputProps, open } = useDropzone({
        onDrop: handleDrop,
        multiple: true,
        maxFiles: 5,
        noClick: true,
        noKeyboard: true,
    });
    const handleAttachFileClick = useCallback(() => {
        open();
    }, [open]);
    // END - File upload

    return (
        <>
            <FormControl>
                <Textarea
                    placeholder="Ask Marvin any question…"
                    aria-label="Message"
                    ref={textAreaRef}
                    onChange={(e) => {
                        setTextAreaValue(e.target.value);
                    }}
                    value={textAreaValue}
                    minRows={3}
                    maxRows={10}
                    autoFocus={true}
                    startDecorator={
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {uploadedFiles.map((file) => (
                                <FileUpload file={file} />
                            ))}
                        </Stack>
                    }
                    endDecorator={
                        <Stack
                            direction="row"
                            justifyContent={"space-between"}
                            flexGrow={1}
                            sx={{
                                py: 1,
                                pr: 1,
                            }}
                        >
                            <Stack direction={"row"}>
                                {showFileUpload && (
                                    <Button
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        sx={{ alignSelf: "center", borderRadius: "sm" }}
                                        startDecorator={<AttachFileRounded />}
                                        onClick={handleAttachFileClick}
                                    >
                                        Attach file
                                    </Button>
                                )}

                                {showFileUpload && (
                                    <div {...getRootProps()} style={{ display: "none" }}>
                                        <input {...getInputProps()} />
                                    </div>
                                )}

                                {showGenerateSpreadsheet && (
                                    <Button
                                        size="sm"
                                        variant="plain"
                                        color="neutral"
                                        sx={{ alignSelf: "center", borderRadius: "sm" }}
                                        startDecorator={<TableViewOutlined />}
                                        onClick={() => setGenerateSpreadsheetModalOpen(true)}
                                    >
                                        Generate spreadsheet
                                    </Button>
                                )}
                            </Stack>

                            <Button
                                size="sm"
                                color="primary"
                                sx={{ alignSelf: "center", borderRadius: "sm" }}
                                endDecorator={<SendRounded />}
                                onClick={handleSendClick}
                                disabled={fileUploadInProgress}
                            >
                                Send
                            </Button>
                        </Stack>
                    }
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !event.shiftKey) {
                            handleSendClick();
                            event.preventDefault();
                        }
                    }}
                    sx={{
                        "& textarea:first-of-type": {
                            minHeight: 72,
                        },
                    }}
                />
            </FormControl>

            <Modal
                open={generateSpreadsheetModalOpen}
                onClose={() => setGenerateSpreadsheetModalOpen(false)}
            >
                <ModalDialog>
                    <ModalClose />
                    <SpreadsheetFlowBuilder
                        onClose={() => setGenerateSpreadsheetModalOpen(false)}
                        onSubmit={(type) => {
                            props.onSubmitGenerateSpreadsheet(type);
                            setGenerateSpreadsheetModalOpen(false);
                        }}
                    />
                </ModalDialog>
            </Modal>
        </>
    );
}
